import type { Transaction } from '@asia/shared';
import { rpc } from './ws';

/** What a scanned code says, before any money is discussed. */
export interface QrLinkInfo {
  merchantName: string | null;
  fiatAmount: number;
  fiatCurrency: string;
  type: string;
  /** A dynamic code carries no amount — the user types one in. */
  amountRequired: boolean;
}

export interface QrQuote {
  link: string;
  merchantName: string | null;
  fiatAmount: number;
  fiatCurrency: string;
  type: string;
  /** USDT the store is owed. */
  amountUsdt: number;
  /** The provider's cut. */
  providerFeeUsdt: number;
  /** Ours, set in the admin panel. */
  serviceFeeUsdt: number;
  totalUsdt: number;
  rate: number;
  token: 'USDT';
  payTo: string;
}

export type QrStatus =
  | 'created'
  | 'pending'
  | 'processing'
  | 'on_review'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'refunded';

export interface QrPayment {
  id: string;
  externalId: string;
  merchantName: string | null;
  mcc: string | null;
  paymentType: string | null;
  fiatAmount: number;
  fiatCurrency: string;
  amountUsdt: number;
  providerFeeUsdt: number;
  serviceFeeUsdt: number;
  totalUsdt: number;
  status: QrStatus;
  statusReason: string | null;
  storeBlacklisted: boolean;
  refundRequired: boolean;
  openUntil: string | null;
  paymentTransferId: string | null;
  /** The on-chain send that funded this — how a history row is matched to it. */
  txHash: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A state the payment can never leave. */
export const isFinal = (s: QrStatus): boolean =>
  s === 'completed' || s === 'failed' || s === 'expired' || s === 'refunded';

export const qrLinkInfoApi = (link: string) => rpc<QrLinkInfo>('qr.linkInfo', { link });

export const qrQuoteApi = (link: string, amount?: string) =>
  rpc<QrQuote>('qr.quote', { link, amount });

/** Open the payment, funded by an already-confirmed USDT transfer. */
export const qrPayApi = (params: {
  link: string;
  amount?: string;
  walletId?: string;
  quotedRate: number;
  paymentTransferId: string;
  idempotencyKey: string;
  reauthToken?: string;
}) => rpc<{ payment: QrPayment }>('qr.pay', params).then((r) => r.payment);

export const qrGetApi = (id: string) =>
  rpc<{ payment: QrPayment }>('qr.get', { id }).then((r) => r.payment);

export const qrListApi = (limit?: number) =>
  rpc<{ payments: QrPayment[] }>('qr.list', { limit }).then((r) => r.payments);

/**
 * A QR payment as a history row.
 *
 * The money left the wallet as an ordinary USDT transfer, so the on-chain row
 * already exists — this only supplies the store details the chain cannot know.
 */
export function qrToTransaction(p: QrPayment): Transaction {
  return {
    id: `qr-${p.id}`,
    type: 'qr',
    status: p.status === 'completed' ? 'confirmed' : isFinal(p.status) ? 'failed' : 'pending',
    coin: 'USDT',
    chain: 'TRON',
    amount: -p.totalUsdt,
    amountUsd: p.totalUsdt,
    feeUsd: p.providerFeeUsdt + p.serviceFeeUsdt,
    date: p.createdAt,
    merchant: p.merchantName ?? undefined,
    mcc: p.mcc ?? undefined,
    paymentType: p.paymentType ?? undefined,
    storeAmount: p.fiatAmount,
    storeCurrency: p.fiatCurrency,
  };
}
